import type { FlattenedThemeType } from '@unifyapps/defs/theme/types';
import type { JavaScriptFunctionState } from '@unifyapps/defs/types/javascriptFunction';
import type { JavaScriptVariableState } from '@unifyapps/defs/types/javascriptVariable';
import { create, type StateCreator, type StoreApi } from 'zustand';
import type { RemountFunction } from '../../../../components/Remount/Remount';
import createObjectSetters from '../../../../utils/setters/createObjectSetters';
import { createTapBinder } from '../../../pulse/createTapBinder';
import type { ScopeId } from '../../../pulse/types';
import createDataSourceControllerSlice from '../../../slices/DataSourceControllerSlice/createDataSourceControllerSlice';
import type { DataSourceRecordStoreState } from '../../DataSourceRecordStore';
import getDeviceVariantActions from './actions/deviceVariantAction';
import getLocalStorageAction from './actions/getLocalStorageAction';
import getSessionStorageAction from './actions/getSessionStorageAction';
import type {
  AppsAndIntegrationsInStore,
  ColorSchemeDetails,
  DeviceContextValue,
  GlobalAppStateStore,
  GlobalAppStateStoreType,
} from './types';

export const createGlobalAppStateStore = ({
  initial,
  getDataSourceRecordsStore,
  remount,
  scopeId,
  capture,
}: {
  getDataSourceRecordsStore: StoreApi<DataSourceRecordStoreState>['getState'];
  initial: GlobalAppStateStore;
  remount: RemountFunction;
  scopeId: ScopeId;
  // false for RUNNER-mode apps so their setters never feed Pulse — see createTapBinder.
  capture: boolean;
}) => {
  const initializer: StateCreator<GlobalAppStateStoreType> = (set, get) => {
    const { setMultiple: setLocation } = createObjectSetters<
      GlobalAppStateStoreType,
      'location',
      string
    >('location', set);

    const setNavigationState = (navigationState: Partial<GlobalAppStateStore['navigation']>) =>
      set((state) => ({
        navigation: { ...state.navigation, ...navigationState },
      }));

    const { setMultiple: setUserContext } = createObjectSetters<
      GlobalAppStateStoreType,
      'userContext',
      string
    >('userContext', set);

    const { setMultiple: setDeviceContext } = createObjectSetters<
      GlobalAppStateStoreType,
      'deviceContext',
      DeviceContextValue
    >('deviceContext', set);

    const setAppsAndIntegrations = (appsAndIntegrations: AppsAndIntegrationsInStore) =>
      set({ appsAndIntegrations });

    const {
      setSingle: setPageVariableState,
      setMultiple: setPageVariablesState,
      remove: rawRemovePageVariableState,
    } = createObjectSetters<GlobalAppStateStoreType, 'pageVariables', JavaScriptVariableState>(
      'pageVariables',
      set,
    );

    const tap = createTapBinder(scopeId, get, capture);
    const exposedSetPageVariableState = tap.mapSetter(
      'pageVariable',
      'pageVariables',
      setPageVariableState,
    );
    const exposedRemovePageVariableState = tap.mapRemover(
      'pageVariable',
      'pageVariables',
      rawRemovePageVariableState,
    );

    const { setSingle: rawSetPageFunctionState, remove: rawRemovePageFunctionState } =
      createObjectSetters<GlobalAppStateStoreType, 'pageFunctions', JavaScriptFunctionState>(
        'pageFunctions',
        set,
      );
    const setPageFunctionState = tap.mapSetter(
      'pageFunction',
      'pageFunctions',
      rawSetPageFunctionState,
    );
    const removePageFunctionState = tap.mapRemover(
      'pageFunction',
      'pageFunctions',
      rawRemovePageFunctionState,
    );

    const setActiveAppVariantKey = (appVariantKey: string) =>
      set({ activeAppVariantKey: appVariantKey });

    const reset = () => {
      set(() => storeObject);
    };

    const { dataSourceControllerState, dataSourceControllerActions } =
      createDataSourceControllerSlice({
        get,
        set,
        getDataSourceRecordsStore,
        scopeId,
        capture,
      });

    const state: GlobalAppStateStore = {
      ...initial,
      ...dataSourceControllerState,
    };

    const storeObject: GlobalAppStateStoreType = {
      ...state,
      actions: {
        setUserContext,
        setDeviceContext,
        setAppsAndIntegrations,
        setLocation,
        setActiveAppVariantKey,
        setNavigationState,
        setDeviceDetails: getDeviceVariantActions(set),
        reset,
        setInterfaceRecord: (interfaceRecord) => set({ interfaceRecord }),
        setCommonNamespace: (commonNamespace) => set({ commonNamespace }),
        setLocalStorage: getLocalStorageAction(get, set),
        setSessionStorage: getSessionStorageAction(get, set),
        remount,
        setPageVariableState: exposedSetPageVariableState,
        setPageVariablesState,
        removePageVariableState: exposedRemovePageVariableState,
        setPageFunctionState,
        removePageFunctionState,
        dataSource: dataSourceControllerActions,
        setColorSchemeDetails: (colorSchemeDetails: ColorSchemeDetails) =>
          set({ colorSchemeDetails }),
        setInterfaceThemeId: (themeId: string | undefined) => set({ interfaceThemeId: themeId }),
        setActiveTheme: (theme?: FlattenedThemeType) => set({ activeTheme: theme }),
        setAvailableThemes: (themes: FlattenedThemeType[]) => set({ availableThemes: themes }),
      },
    };

    return storeObject;
  };
  return create<GlobalAppStateStoreType>()(initializer);
};
